import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Heart, Send } from 'lucide-react';

const formSchema = z.object({
  email: z.string().trim().email("Invalid email address").max(255, "Email too long"),
  interestType: z.enum(['participant', 'expert', 'venue_owner', 'newsletter']),
  firstName: z.string().trim().max(100, "First name too long").optional(),
  lastName: z.string().trim().max(100, "Last name too long").optional(),
  phone: z.string().trim().max(20, "Phone number too long").optional(),
  website: z.string().trim().max(500, "Website URL too long").optional(),
  description: z.string().trim().max(2000, "Description too long").optional()
});

type FormData = z.infer<typeof formSchema>;
interface JoinMovementModalProps {
  trigger: React.ReactNode;
}
export const JoinMovementModal = ({
  trigger
}: JoinMovementModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: {
      errors
    }
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      interestType: 'participant'
    }
  });

  const interestType = watch('interestType');
  const showExtraFields = interestType === 'expert' || interestType === 'venue_owner';

  const onSubmit = async (data: FormData) => {
    try {
      // Use edge function with rate limiting instead of direct DB insert
      const { data: result, error } = await supabase.functions.invoke('submit-movement', {
        body: {
          email: data.email,
          interestType: data.interestType,
          firstName: data.firstName || null,
          lastName: data.lastName || null,
          phone: data.phone || null,
          website: data.website || null,
          description: data.description || null
        }
      });

      if (error) {
        // Handle rate limiting error
        if (error.message?.includes('429') || error.message?.includes('Too many')) {
          toast.error("Too many submissions", {
            description: "Please try again in an hour. We've received your interest!",
          });
          return;
        }
        throw error;
      }

      // Send email notification
      await supabase.functions.invoke('send-notification', {
        body: {
          type: 'movement_submission',
          data: {
            email: data.email,
            interestType: data.interestType,
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            website: data.website,
            description: data.description
          }
        }
      });

      toast.success('Thank you for joining Mystic Forest Club. You\'ll hear from us soon — new retreats, stories, and opportunities to connect are on the way.');
      reset();
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to submit form. Please try again.');
    }
  };
  return <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center pb-6">
          <div className="mx-auto w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-accent" />
          </div>
          <DialogTitle className="text-2xl font-serif font-light">Join the Club</DialogTitle>
          <DialogDescription className="text-muted-foreground mt-2">
            Be part of Mystic Forest. Leave your email to receive updates on new retreats, blog posts, and our journey — and let us know how you'd like to connect with us.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <Label htmlFor="email" className="text-foreground">Email Address *</Label>
            <Input 
              id="email" 
              type="email" 
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: 'Please enter a valid email address'
                }
              })} 
              placeholder="you@example.com" 
              className="mt-2" 
            />
            {errors.email && <p className="text-destructive text-sm mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <Label className="text-foreground mb-3 block">How would you like to connect? *</Label>
            <Controller
              name="interestType"
              control={control}
              rules={{ required: 'Please select an option' }}
              render={({ field }) => (
                <RadioGroup onValueChange={field.onChange} value={field.value} className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="participant" id="participant" className="mt-0.5" />
                    <Label htmlFor="participant" className="font-normal cursor-pointer leading-tight">
                      I'd love to join future retreats & workshops
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="expert" id="expert" className="mt-0.5" />
                    <Label htmlFor="expert" className="font-normal cursor-pointer leading-tight">
                      I run workshops/events and would like to collaborate
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="venue_owner" id="venue_owner" className="mt-0.5" />
                    <Label htmlFor="venue_owner" className="font-normal cursor-pointer leading-tight">
                      I have a venue and host retreats/events
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="newsletter" id="newsletter" className="mt-0.5" />
                    <Label htmlFor="newsletter" className="font-normal cursor-pointer leading-tight">
                      I just want to stay in touch & get updates
                    </Label>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.interestType && <p className="text-destructive text-sm mt-1">{errors.interestType.message}</p>}
          </div>

          {showExtraFields && (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-foreground">First Name *</Label>
                  <Input 
                    id="firstName" 
                    {...register('firstName', {
                      required: showExtraFields ? 'First name is required' : false
                    })} 
                    placeholder="Your first name" 
                    className="mt-2" 
                  />
                  {errors.firstName && <p className="text-destructive text-sm mt-1">{errors.firstName.message}</p>}
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-foreground">Last Name *</Label>
                  <Input 
                    id="lastName" 
                    {...register('lastName', {
                      required: showExtraFields ? 'Last name is required' : false
                    })} 
                    placeholder="Your last name" 
                    className="mt-2" 
                  />
                  {errors.lastName && <p className="text-destructive text-sm mt-1">{errors.lastName.message}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-foreground">Phone Number</Label>
                <Input 
                  id="phone" 
                  type="tel" 
                  {...register('phone')} 
                  placeholder="+1 234 567 8900" 
                  className="mt-2" 
                />
              </div>

              <div>
                <Label htmlFor="website" className="text-foreground">Website or Social Media</Label>
                <Input 
                  id="website" 
                  {...register('website')} 
                  placeholder="https://yourwebsite.com or @yourhandle" 
                  className="mt-2" 
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-foreground">
                  Short description of your {interestType === 'expert' ? 'work' : 'venue'}
                </Label>
                <Textarea 
                  id="description" 
                  rows={4} 
                  {...register('description')} 
                  placeholder={`Tell us about your ${interestType === 'expert' ? 'expertise and the workshops you offer' : 'venue and what makes it special'}...`}
                  className="mt-2" 
                />
              </div>
            </>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button type="submit" size="lg" className="flex-1 px-8 py-3 bg-accent hover:bg-accent/90 text-accent-foreground">
              <Send className="w-5 h-5 mr-2" />
              Join the Club
            </Button>
            <Button type="button" variant="outline" size="lg" onClick={() => setIsOpen(false)} className="px-8">
              Maybe Later
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>;
};